const destinations = [
  {
    name: "Japan &amp; South Korea",
    context: "JPL partnerships with JAXA, Caltech academic collaborations, Rose Bowl-inspired family trips",
    urgency: "Academic conference invitations arrive 2–6 weeks before departure",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Germany &amp; Switzerland",
    context: "ESA facility visits in Darmstadt, CERN collaborations, Max Planck Institute research",
    urgency: "International research trips confirmed weeks out, not months",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "China, Taiwan &amp; Hong Kong",
    context: "San Gabriel Valley family travel for Lunar New Year, Golden Week, and urgent family visits",
    urgency: "Holiday travel peaks create passport agency backlogs in Jan and Oct",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "France &amp; Italy",
    context: "Wine industry travel, European heritage tourism, Caltech humanities programs",
    urgency: "Academic program deadlines and wine trade shows create fixed windows",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Chile &amp; Hawaii",
    context: "Caltech Palomar / Las Campanas Observatory field trips, family vacations from LAX",
    urgency: "Telescope observation schedules are non-negotiable and cannot be rescheduled",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Mexico &amp; Caribbean",
    context: "San Gabriel Valley family visits, resort travel, medical tourism from Arcadia",
    urgency: "Last-minute bookings and airline fare deals trigger fast travel decisions",
    icon: "ri-map-pin-2-line",
  },
];

const commonSituations = [
  {
    stat: "71%",
    label: "of Pasadena expedited requests involve academic, research, or professional travel with non-negotiable departure windows",
  },
  {
    stat: "2–3 Weeks",
    label: "average notice Caltech and JPL travelers have before international research trips or conference invitations",
  },
  {
    stat: "Jan & Oct",
    label: "peak San Gabriel Valley passport urgency months tied to Lunar New Year and Chinese Golden Week travel",
  },
  {
    stat: "10 Miles",
    label: "distance to the LA Passport Agency — but getting a same-day appointment is rarely guaranteed even nearby",
  },
];

export default function PasadenaDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Pasadena Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from San Gabriel Valley clients. Understanding local travel behavior is part of why our document review catches issues before they derail your research trip, conference, or family vacation.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm" dangerouslySetInnerHTML={{ __html: d.name }}></h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other California Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across California with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/los-angeles" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Los Angeles <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/glendale" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Glendale <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/burbank" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Burbank <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}